import { Artwork, Badge, Base, Button, Column, FocusManager, NavigationManager, ProgressBar, ScrollWrapper, TextBox, utils, withSelections } from "@lightningjs/ui-components";
import EpgChannel from "../epg-channel";
import EPGManager from "../epg-model";
import EPGRowUI from "./epg-row-ui";
import EPGUtility from "../epg-utility";
import EPGConfig from "../epg-config";
import { getImage } from "../../../models/data-service";
import { AppData, Img, Lightning, Utils } from "@lightningjs/sdk";
import { Helper } from "../../../helper";
import GradientBottom from "./gradient-bottom";
import EPGTimeRowUI from "./epg-time-row-ui";
import { AppSetup } from "../../../app-setup";
import EPGRowLabelUI from "./epg-row-label-ui";
import EPGChannelIconUI from "./epg-channel-icon-ui";

const TIMELINE_ARROW_SIZE = 56
const TIMELINE_ARROW_LEFT_X = -11
const TIMELINE_ARROW_RIGHT_INSET = 113
const PROGRAM_WRAPPER_W_WITH_ROW_LABEL = 1421

const timelineArrowTemplate = (rotation: number, layout: { x: number; mountX?: number }) => ({
    y: 8,
    w: TIMELINE_ARROW_SIZE,
    h: TIMELINE_ARROW_SIZE,
    collision: true,
    zIndex: 5,
    visible: false,
    ...layout,
    Icon: {
        mountX: 0.5,
        mountY: 0.5,
        x: (w: number) => w / 2,
        y: (h: number) => h / 2,
        rotation,
        texture: {
            src: Utils.asset('images/ico-up-arrow-epg-v1.png'),
            resizeMode: { w: 31, h: 14, type: 'contain' },
            type: Lightning.textures.ImageTexture
        }
    }
})

export class EPGRatingBadge extends Badge {
    static get __componentName() { return 'EPGRatingBadge'; }

    _updateWidth() {
        const s = this as any
        let width = 0
        if (s.title && s.icon) {
            width = s._Text.renderWidth + s._Icon.finalW + s.style.contentSpacing + s.style.paddingX * 2
        } else if (s.title) {
            width = s._Text.renderWidth + s.style.paddingX * 2
        } else if (s.icon) {
            width = s._Icon.finalW + s.style.paddingX * 2
        }
        s.w = Math.max(width, s.style.minWidth || 0)
    }

    _updateBackground() {
        const s = this as any
        const height = Math.max(s._Text.renderHeight, s._Icon.h) + s.style.paddingY * 2
        const clampedHeight = Math.max(height, s.style.minHeight || 0)

        s.patch({
            h: clampedHeight,
            texture: Lightning.Tools.getRoundRect(
                s.w,
                clampedHeight,
                utils.getMaxRoundRadius(s.style.radius, s.w, clampedHeight),
                s.style.strokeWidth,
                s.style.strokeColor,
                true,
                s.style.backgroundColor
            )
        })
    }

    _updatePositions() {
        const s = this as any
        s._Icon.y = s.h / 2

        if (s.iconAlign === 'left' && s.title && s.icon) {
            s._Icon.x = s.style.paddingX
            s._Text.x = s._Icon.x + s._Icon.finalW + s.style.contentSpacing
        } else if (s.iconAlign === 'right' && s.title && s.icon) {
            s._Text.x = s.style.paddingX
            s._Icon.x = s._Text.x + s._Text.renderWidth + s.style.contentSpacing
        } else if (s.title) {
            s._Text.x = (s.w - s._Text.renderWidth) / 2
            s._Icon.x = s.style.paddingX
        } else {
            s._Text.x = s.style.paddingX
            s._Icon.x = s.style.paddingX
        }

        s._Text.y = s._h / 2 + s.style.offsetY
    }
}

class EPGColumnFocusManager extends Column {
    _canNavigateFn: (() => boolean) | null = null;

    static get __componentName() { return 'EPGColumnFocusManager'; }

    onDown() {
        if (this._canNavigateFn && !this._canNavigateFn()) return true;
        return (this as any).selectNext(false);
    }

    onUp() {
        if (this._canNavigateFn && !this._canNavigateFn()) return true;
        return (this as any).selectPrevious();
    }
}

export default class EPGGuide extends Base<Base.TemplateSpec> {
    [x: string]: any;

    epgChannelCreated = false;
    prevIndex = -1
    _playingLocalIndex = 0   // local index (within active row) of the channel currently playing
    _highlightedIconIdx = -1 // index of the ONE channel icon currently tinted, -1 when none
    timestamp = 0;
    imageUpdateId: any = null;
    _currentImgUrl = '';
    autoHideId;
    autoHideTime = 8 * 1000; // 8 second timer, re-armed on every key press
    _autoHideMs = 0;
    focusTime!: Date
    allRowNames!: string[];
    _activeRowName!: string;
    _channelIconFocused = false;
    _keepEpgExpanded = false;
    _epgReturnState = '';
    _isLoading = false;
    _lastNavTime = 0;
    _navThrottleMs = 300;
    _lastScrollTime = 0;
    _scrollThrottleMs = 350;
    _focusColor: number = 0xffffffff;
    _timelineCache: { key: number; items: any[] } | null = null;

    set activeRowName(val: string) {
        if (this._activeRowName !== val) {
            this._activeRowName = val;
            this.epgChannelCreated = false;
            this._update();
        }
    }

    get activeRowName(): string {
        return this._activeRowName;
    }

    get _showRowLabel(): boolean {
        return !!(this.allRowNames && this.allRowNames.length > 1);
    }

    static override _template() {
        return {
            w: (w: number) => w,
            h: (h: number) => h,
            EPGHitArea: {
                x: 0,
                y: 0,
                w: (w: number) => w,
                h: (h: number) => h,
                rect: true,
                color: 0x00000000,
                collision: 2,
                zIndex: 0,
            },
            Info: {
                zIndex: 2,
                w: 1920,
                h: 250,
                collision: 2,
                Image: {
                    x: 44,
                    w: 448,
                    h: 250,
                    collision: 2,
                    FocusRing: {
                        x: -4, y: 62,
                        w: 317, h: 182,
                        rect: true,
                        color: 0x00ffffff,
                        collision: true,
                        shader: { type: Lightning.shaders.RoundedRectangle, radius: 10, stroke: 4, strokeColor: 0xffffffff }
                    },
                    ArrowUp: {
                        x: 154,
                        y: 0,
                        mountX: 0.5,
                        mountY: 0.5,
                        w: 19,
                        h: 12,
                        src: Utils.asset('images/ico-up-arrow-epg-v1.png'),
                        visible: false
                    },
                    BrowseChannelsText: {
                        x: 154,
                        y: 46,
                        mountX: 0.5,
                        mountY: 1,
                        visible: false,
                        text: {
                            text: 'Browse Channels',
                            fontSize: 25,
                            textAlign: 'center',
                            textColor: 0xffffffff,
                            fontFace: Helper.getFontFamily('Regular')
                        }
                    },
                    Img: {
                        x: 0,
                        y: 66,
                        w: 309,
                        h: 174,
                        type: Artwork,
                        src: '',
                        style: { radius: 20 }
                    }
                },
                GenreRating: {
                    x: 389,
                    y: 80,
                    mountY: 0.5,
                    flex: { direction: 'row', alignItems: 'center' },
                    RatingBadge: {
                        type: EPGRatingBadge,
                        title: '',
                        style: {
                            minWidth: 91,
                            minHeight: 40,
                            paddingX: 12,
                            paddingY: 4,
                            radius: 10,
                            strokeWidth: 1,
                            strokeColor: 0x80ffffff,
                            backgroundColor: 0xe60d0d1d,
                            textStyle: { textColor: 0xffffffff, fontSize: 18, fontFace: Helper.getFontFamily('Regular') }
                        },
                        visible: false
                    },
                    GenreText: {
                        text: { text: '', fontSize: 24, fontFace: Helper.getFontFamily('Regular'), textColor: 0xffffffff }
                    }
                },
                Title: {
                    x: 389,
                    y: 137,
                    mountY: 0.5,
                    text: { text: '', fontSize: 44, fontWeight: 900, fontFace: Helper.getFontFamily('Black'), wordWrapWidth: 1348, maxLines: 1, maxLinesSuffix: '...', textOverflow: 'ellipsis' },
                    color: 0xffffffff
                },
                ProgressWrapper: {
                    x: 389,
                    y: 180,
                    w: 1285,
                    h: 60,
                    flex: { direction: 'column' },
                    TimeWrapper: {
                        w: (w: number) => w,
                        h: 50,
                        flex: { padding: 0, justifyContent: 'space-between', alignItems: 'center' },
                        ProgStartTime: {
                            h: 30,
                            text: { text: '00:00:00', fontSize: 24, fontFace: "Light" }
                        },
                        ProgEndTime: {
                            h: 30,
                            text: { text: '00:00:00', fontSize: 24, fontFace: "Light" }
                        }
                    },
                    Progressbar: {
                        w: 1285,
                        type: ProgressBar,
                        progress: 25,
                        style: {
                            height: 8,
                            barColor: 0x1EFFFFFF,
                            progressColor: 0xffff0000,
                            radius: 0
                        }
                    },
                },
                InfoIcon: {
                    x: 1722,
                    y: 226,
                    w: 101,
                    h: 101,
                    mountY: 0.5,
                    collision: true,
                    texture: {
                        type: Lightning.textures.ImageTexture,
                        src: Utils.asset('images/player_control_sprite_updated.png'),
                        x: 1440,
                        y: 0,
                        w: 144,
                        h: 144,
                        resizeMode: { type: 'contain', w: 101, h: 101 }
                    }
                }
            },
            GradientBottom: {
                y: 0,
                zIndex: 1,
                type: GradientBottom
            },
            FullLoadingOverlay: {
                x: 0, y: 0,
                w: (w: number) => w,
                h: (h: number) => h,
                zIndex: 30, rect: true, color: 0xFF000000,
                visible: false,
                LoadingText: {
                    mountX: 0.5, mountY: 0.5,
                    x: 960, y: 200,
                    text: {
                        text: 'Loading...',
                        fontSize: 32,
                        fontFace: Helper.getFontFamily('Regular'),
                        textColor: 0xFFFFFFFF
                    }
                }
            },
            EpgWrapper: {
                zIndex: 2,
                x: 44,
                y: 250,
                h: 572,
                w: 1876,
                collision: 2,
                RowLabelWrapper: {
                    x: 0,
                    h: 572,
                    w: EPGConfig.RowLabelWidth,
                    collision: 2,
                    RowLabelHeading: {
                        x: 0,
                        y: 0,
                        h: 72,
                        w: EPGConfig.RowLabelWidth,
                        DateText: {
                            type: TextBox,
                            w: EPGConfig.RowLabelWidth,
                            content: EPGUtility.displayToday(),
                            style: {
                                offsetY: 20,
                                textStyle: {
                                    fontSize: 22,
                                    fontFace: Helper.getFontFamily('Black'),
                                    textColor: 0x99ffffff,
                                    textAlign: 'center'
                                }
                            }
                        }
                    },
                    RowLabelColumn: {
                        x: -20,
                        y: 72,
                        h: 500,
                        clipping: true,
                        w: EPGConfig.RowLabelWidth + 20,
                        type: Column,
                        collision: 2,
                        style: {
                            itemSpacing: 0
                        },
                        signals: {
                            'selectedChange': 'selectedChangeRowLabel'
                        }
                    }
                },
                ChannleNameWrapper: { //column
                    x: 345,
                    h: 572,
                    w: EPGConfig.ChannelWidth,
                    collision: 2,
                    LoadingOverlay: {
                        x: 0, y: 0,
                        w: (w: number) => w,
                        h: (h: number) => h,
                        zIndex: 30, rect: true, color: 0xCC000000,
                        visible: false
                    },
                    TimelineArrowLeft: timelineArrowTemplate(-Math.PI / 2, { x: TIMELINE_ARROW_LEFT_X }),
                    TimelineChannelHeading: {
                        x: 0,
                        y: 72,
                        h: 500,
                        clipping: true,
                        w: EPGConfig.ChannelWidth,
                        type: Column,
                        collision: 2,
                        style: {
                            itemSpacing: 10
                        }
                    }
                },
                ChannleProgramWrapper: { //program
                    x: 455,
                    h: 572,
                    w: 1421,
                    clipping: true,
                    collision: 2,
                    LoadingOverlay: {
                        x: 0, y: 0,
                        w: (w: number) => w,
                        h: (h: number) => h,
                        zIndex: 30, rect: true, color: 0xCC000000,
                        visible: false,
                        LoadingText: {
                            mountX: 0.5, mountY: 0.5,
                            x: (w: number) => w / 2,
                            y: (h: number) => h / 2,
                            text: {
                                text: 'Loading...',
                                fontSize: 32,
                                fontFace: Helper.getFontFamily('Regular'),
                                textColor: 0xFFFFFFFF
                            }
                        }
                    },
                    Timeline: {
                        x: 0,
                        y: 0,
                        h: 72,
                        w: 3 * EPGConfig.pixelPer30m,
                        clipping:true,
                        type: EPGTimeRowUI,
                        zIndex: 2,
                        signals: {
                            'selectedChange': 'selectedChangeTimeline'
                        }
                    },
                    // x is re-patched in _updateChannel() because the wrapper width is layout-dependent.
                    TimelineArrowRight: timelineArrowTemplate(Math.PI / 2, {
                        mountX: 1,
                        x: PROGRAM_WRAPPER_W_WITH_ROW_LABEL - TIMELINE_ARROW_RIGHT_INSET
                    }),
                    ChannelContainer: {
                        x: 0,
                        y: 72,
                        h: 500,
                        w: 1920,
                        clipping: true,
                        collision: 2,
                        ChannelFocusManager: {
                            x: 0,
                            y: 0,
                            h: 500,
                            w: 1920,
                            type: EPGColumnFocusManager,
                            collision: 2,
                            plinko: false,
                            style: {
                                itemSpacing: 0
                            },
                            signals: {
                                'selectedChange': '_onChannelRowChanged'
                            }
                        },
                        CurrentTimeLineWrapper: {
                            x: 0,
                            y: 0,
                            h: 500,
                            zIndex: 8,
                            CurrTimeLine: {
                                x: 0,
                                y: 0,
                                rect: true,
                                w: 3,
                                h: 115,
                                color: 0xffffffff,
                            }
                        }
                    }
                }
            }
        }
    }

    static get __componentName(): string {
        return 'EPGGuide'
    }

    static get tags() {
        return ['TimelineChannelHeading', 'ChannelWrapper', 'ChannelFocusManager', 'Timeline', 'RowLabelColumn']
    }

    static get properties() {
        return ['epg', 'allRowNames']
    }

    override _init() {
        this.focusTime = EPGUtility.getFocusTime()
        ;[this._RowLabelColumn, this._ChannelFocusManager, this._TimelineChannelHeading, this._Timeline].forEach((c: any) => {
            if (c?.style) c.style.itemTransition = { duration: 0 }
        })

        ;(this as any).tag('Progressbar').style = { height: 8, barColor: 0x1EFFFFFF, progressColor: this._getFocusColor(), radius: 0 }
        ;(this._ChannelFocusManager as EPGColumnFocusManager)._canNavigateFn = this._canNavigate.bind(this)

        if (this.showGridArrowButtons) {
            const s = this as any
            s.tag('EpgWrapper.ChannleNameWrapper.TimelineArrowLeft').visible = true
            s.tag('EpgWrapper.ChannleProgramWrapper.TimelineArrowRight').visible = true
        }

        this._setState('ThumbnailState')
    }

    /** Same platform gate the main grid's row arrows use (RowItem.showGridArrowButtons) */
    get showGridArrowButtons() {
        return (AppData as AppSetup).platform?.showGridArrowButtons() || false
    }

    override _detach() {
        this.stopAutoHideTimeout()
        clearTimeout(this.imageUpdateId)
    }

    override _focus() {
        this._setState('')
        this._setState('ThumbnailState')
    }

    override _unfocus() {
        this.stopAutoHideTimeout()
        this._showThumbnailFocus(false)
        this._showInfoButtonFocus(false)
        this.tag('Info.Image.ArrowUp').patch({ visible: false, rotation: 0 })
        this.tag('Info.Image.BrowseChannelsText').visible = false
        if (this._ChannelFocusManager && this._ChannelFocusManager.selectedIndex !== undefined) {
            this._highlightChannelIcon(this._ChannelFocusManager.selectedIndex, false)
        }
    }

    /** Called by parent to enter GridState without triggering focus side-effects from outside */
    enterGridState() {
        this._setState('GridState');
    }

    /** Sets _activeRowName without triggering the full setter's _update() side-effect */
    setActiveRowNameSilent(name: string) {
        this._activeRowName = name;
    }

    /** Called by parent to reset focus to thumbnail (e.g. when EPG re-appears) */
    resetFocusToThumbnail() {
        this._keepEpgExpanded = false
        this._epgReturnState = ''
        if (this._ChannelFocusManager && this._playingLocalIndex >= 0) {
            this._ChannelFocusManager.selectedIndex = this._playingLocalIndex
            if (this._TimelineChannelHeading) {
                this._TimelineChannelHeading.selectedIndex = this._playingLocalIndex
            }
            const filteredChannels = this.allRowNames?.length
                ? this._epg?.EPGChannelList?.filter((ch: EpgChannel) => ch.rowName === this.activeRowName)
                : this._epg?.EPGChannelList
            const playingChannel = filteredChannels?.[this._playingLocalIndex]
            if (playingChannel && this._epg) {
                this._epg.currIndex = playingChannel.index
            }
        }
        this._setState('')
        this._setState('ThumbnailState')
    }

    _showArrow(rotation: number) {
        if (this._isLoading) return;
        (this as any).tag('Info.Image.ArrowUp').patch({ visible: true, rotation });
    }

    // fullOverlay=true  → initial entry: black overlay over the entire EPGGuide
    // fullOverlay=false → row switch: dimming overlay on channel column + program tiles only
    setLoading(val: boolean, fullOverlay = true) {
        this._isLoading = val;
        const s = this as any;
        if (val) {
            s.tag('Info.Image.ArrowUp').visible = false;
            this.stopAutoHideTimeout();
            if (fullOverlay) {
                s.tag('FullLoadingOverlay').visible = true;
            } else {
                s.tag('EpgWrapper.ChannleNameWrapper.LoadingOverlay').visible = true;
                s.tag('EpgWrapper.ChannleProgramWrapper.LoadingOverlay').visible = true;
            }
        } else {
            s.tag('FullLoadingOverlay').visible = false;
            s.tag('EpgWrapper.ChannleNameWrapper.LoadingOverlay').visible = false;
            s.tag('EpgWrapper.ChannleProgramWrapper.LoadingOverlay').visible = false;
            this.startAutoHideTimeout(5000)
            this._showArrow(this._isExpanded ? Math.PI : 0);
        }
    }

    startAutoHideTimeout(ms = this.autoHideTime) {
        this.stopAutoHideTimeout()
        this._autoHideMs = ms
        this.autoHideId = setTimeout(() => {
            this._autoHideMs = 0
            this.fireAncestors('$hideEpgGuide');
        }, ms)
    }

    stopAutoHideTimeout() {
        this._autoHideMs = 0
        clearTimeout(this.autoHideId)
    }
    override _captureKey() {
        const ms = this._autoHideMs
        if (ms > 0) {
            this.startAutoHideTimeout(ms)
        }
        return false
    }

    /** Show thumbnail focus ring */
    _showThumbnailFocus(focused: boolean) {
        this.tag('Info.Image.FocusRing').patch({
            shader: { type: Lightning.shaders.RoundedRectangle, radius: 10, stroke: focused ? 4 : 0, strokeColor: this._getFocusColor() }
        })
    }
    
    _getFocusColor(): number {
        const { appState } = AppData as AppSetup;
        this._focusColor = Helper.getHexaColor(
            appState?.getAppApiConfig()?.appLayoutConfig.textfocuscolor
        ) || 0xffffffff;
        return this._focusColor
    }

    /** Show InfoIcon focus using sprite row — same pattern as ControlIcon */
    _showInfoButtonFocus(focused: boolean) {
        this.tag('Info.InfoIcon').patch({
            texture: {
                type: Lightning.textures.ImageTexture,
                src: Utils.asset('images/player_control_sprite_updated.png'),
                x: 1440,
                y: focused ? 144 : 0,
                w: 144,
                h: 144,
                resizeMode: { type: 'contain', w: 101, h: 101 }
            }
        })
    }

    /** Navigate focus down to the current playing program in EPG grid */
    _focusCurrentProgram() {
        this._showThumbnailFocus(false)
        this._showInfoButtonFocus(false)
        this._setState('GridState')
        this.startAutoHideTimeout()
    }

    _canNavigate(): boolean {
        const now = Date.now()
        if (now - this._lastNavTime < this._navThrottleMs) return false
        this._lastNavTime = now
        return true
    }

    _canScroll(): boolean {
        const now = Date.now()
        if (now - this._lastScrollTime < this._scrollThrottleMs) return false
        this._lastScrollTime = now
        return true
    }

    static override _states() {
        return [
            class ThumbnailState extends this {
                override $enter() {
                    this._showArrow(0)
                    this.tag('Info.Image.BrowseChannelsText').visible = true
                    this._showThumbnailFocus(true)
                    this._showInfoButtonFocus(false)
                    this.startAutoHideTimeout(5000)
                    // Only collapse EPG when not transitioning from the grid/row-label at row 0
                    if (!this._keepEpgExpanded) {
                        this.fireAncestors('$expandEpg', false)
                    }
                    this._updateNowPlayingThumbnail()
                }
                override $exit() {
                    this._showThumbnailFocus(false)
                }
                override _handleLeft() {
                    this.startAutoHideTimeout()
                    return true
                }
                override _handleRight() {
                    this.startAutoHideTimeout()
                    this._setState('InfoButtonState')
                    return true
                }
                override _handleDown() {
                    this._focusCurrentProgram()
                    return true
                }
                override _handleUp() {
                    this.startAutoHideTimeout()
                    if (!this._epgReturnState) {
                        this._focusCurrentProgram()
                    }
                    return true
                }
                override _handleEnter() {
                    if (this._isLoading) return true
                    this.stopAutoHideTimeout()
                    ;(this as any).fireAncestors('$openChannelList')
                    return true
                }
                override _getFocused() {
                    return this
                }
            },

            class InfoButtonState extends this {
                override $enter() {
                    this._showArrow(0)
                    this.tag('Info.Image.BrowseChannelsText').visible = true
                    this._showThumbnailFocus(false)
                    this._showInfoButtonFocus(true)
                    this.startAutoHideTimeout()
                    if (!this._keepEpgExpanded) {
                        this.fireAncestors('$expandEpg', false)
                    }
                }
                override $exit() {
                    this._showInfoButtonFocus(false)
                }
                override _handleLeft() {
                    this.startAutoHideTimeout()
                    this._setState('ThumbnailState')
                    return true
                }
                override _handleRight() {
                    this.startAutoHideTimeout()
                    return true
                }
                override _handleUp() {
                    this.startAutoHideTimeout()
                    return true
                }
                override _handleDown() {
                    this._focusCurrentProgram()
                    return true
                }
                override _handleEnter() {
                    if (this._isLoading) return true
                    this.stopAutoHideTimeout()
                    ;(this as any).fireAncestors('$openChannelList')
                    return true
                }
                override _getFocused() {
                    return this
                }
            },

            class GridState extends this {
                override $enter() {
                    this._showArrow(Math.PI)
                    this.tag('Info.Image.BrowseChannelsText').visible = true
                    this._showThumbnailFocus(false)
                    this._showInfoButtonFocus(false)
                    this.startAutoHideTimeout()
                    this.fireAncestors('$expandEpg', true)
                }
                override _handleUp() {
                    this.startAutoHideTimeout()
                    if (this._ChannelFocusManager.selectedIndex === 0) {
                        this._enterThumbnailKeepExpanded('GridState')
                        return true
                    }
                    return false
                }
                override _handleLeft() {
                    this.startAutoHideTimeout()
                    return false
                }
                override _handleRight() {
                    this.startAutoHideTimeout()
                    return false
                }
                override _handleDown() {
                    this.startAutoHideTimeout()
                    return false
                }
                override _handleEnter() {
                    this.startAutoHideTimeout()
                    const selected: EPGRowUI = this._ChannelFocusManager?.selected
                    if (selected) {
                        ;(this as any).fireAncestors('$changeChannel', selected._channelIndex)
                    }
                    return true
                }
                override _getFocused() {
                    return this._ChannelFocusManager
                }
            },

            class ChannelIconState extends this {
                override $enter() {
                    this._showArrow(Math.PI)
                    this.tag('Info.Image.BrowseChannelsText').visible = true
                    this._showThumbnailFocus(false)
                    this._showInfoButtonFocus(false)
                    this.startAutoHideTimeout()
                    this.fireAncestors('$expandEpg', true)
                    this._channelIconFocused = true
                    const idx = this._ChannelFocusManager.selectedIndex
                    this._highlightChannelIcon(idx, true)
                    this._updateChannelThumbnail(idx)
                }
                override $exit() {
                    this._channelIconFocused = false
                    const idx = this._ChannelFocusManager.selectedIndex
                    this._highlightChannelIcon(idx, false)
                    this._restoreProgramThumbnail()
                }
                override _handleRight() {
                    this.startAutoHideTimeout()
                    this._setState('GridState')
                    return true
                }
                override _handleLeft() {
                    this.startAutoHideTimeout()
                    if (this._showRowLabel) {
                        this._setState('RowLabelState')
                    }
                    return true
                }
                override _handleUp() {
                    this.startAutoHideTimeout()
                    if (!this._canNavigate()) return true
                    const oldIdx = this._ChannelFocusManager.selectedIndex
                    this._ChannelFocusManager.selectPrevious()
                    const newIdx = this._ChannelFocusManager.selectedIndex
                    if (oldIdx !== newIdx) {
                        this._switchChannelIconFocus(oldIdx, newIdx)
                    } else if (oldIdx === 0) {
                        this._enterThumbnailKeepExpanded('ChannelIconState')
                    }
                    return true
                }
                override _handleDown() {
                    this.startAutoHideTimeout()
                    if (!this._canNavigate()) return true
                    const oldIdx = this._ChannelFocusManager.selectedIndex
                    this._ChannelFocusManager.selectNext()
                    const newIdx = this._ChannelFocusManager.selectedIndex
                    if (oldIdx !== newIdx) {
                        this._switchChannelIconFocus(oldIdx, newIdx)
                    }
                    return true
                }
                override _handleEnter() {
                    this.startAutoHideTimeout()
                    const idx = this._ChannelFocusManager.selectedIndex
                    const row = this._ChannelFocusManager?.items[idx]
                    if (row?.channel) {
                        ;(this as any).fireAncestors('$changeChannel', idx)
                    }
                    return true
                }
                override _getFocused() {
                    return this
                }
            },

            class RowLabelState extends this {
                override $enter() {
                    this._showArrow(Math.PI)
                    this.tag('Info.Image.BrowseChannelsText').visible = true
                    this._showThumbnailFocus(false)
                    this._showInfoButtonFocus(false)
                    this.startAutoHideTimeout()
                    this.fireAncestors('$expandEpg', true)

                    if (this.allRowNames && this.allRowNames.length > 0) {
                        const activeRowIndex = this.allRowNames.indexOf(this.activeRowName);
                        if (activeRowIndex >= 0) {
                            this._RowLabelColumn.selectedIndex = activeRowIndex;
                        }
                    }
                }
                override _handleUp() {
                    this.startAutoHideTimeout()
                    if (this._RowLabelColumn.selectedIndex === 0) {
                        this._enterThumbnailKeepExpanded('RowLabelState')
                        return true
                    }
                    return false
                }
                override _handleRight() {
                    this.startAutoHideTimeout()
                    if (this._isLoading) return true
                    this._setState('ChannelIconState')
                    return true
                }
                override _handleDown() {
                    this.startAutoHideTimeout()
                    return true
                }
                override _handleLeft() {
                    this.startAutoHideTimeout()
                    return true
                }
                override _handleEnter() {
                    this.startAutoHideTimeout()
                    if (this._isLoading) return true
                    const index = this._RowLabelColumn.selectedIndex;
                    const newRowName = this.allRowNames[index];
                    if (newRowName && this.activeRowName !== newRowName) {
                        this._activeRowName = newRowName;
                        this._updateRowLabelHighlight(newRowName);
                        this.epgChannelCreated = false;
                        ;(this as any).fireAncestors('$epgRowChanged', newRowName);
                    }
                    return true
                }
                override _getFocused() {
                    return this._RowLabelColumn
                }
            }
        ]
    }

    /** Highlight/unhighlight the channel icon at a given row index */
    _highlightChannelIcon(index: number, focused: boolean) {
        const iconItems = this._TimelineChannelHeading?.items
        if (!iconItems) return
        if (this._highlightedIconIdx >= 0 && this._highlightedIconIdx !== index) {
            iconItems[this._highlightedIconIdx]?.setHighlight(false)
        }
        if (iconItems[index]) {
            iconItems[index].setHighlight(focused)
        }
        this._highlightedIconIdx = focused && iconItems[index] ? index : -1
    }

    _switchChannelIconFocus(oldIdx: number, newIdx: number) {
        this._highlightChannelIcon(oldIdx, false)
        this._highlightChannelIcon(newIdx, true)
        this._TimelineChannelHeading.selectedIndex = newIdx
        this._updateChannelThumbnail(newIdx)
    }

    get _isExpanded(): boolean {
        const s = (this as any)._getState?.() ?? ''
        return s === 'GridState' || s === 'ChannelIconState' || s === 'RowLabelState'
    }

    _getFilteredChannels(): EpgChannel[] {
        if (!this._epg) return []
        if (this.allRowNames?.length) {
            return this._epg.EPGChannelList.filter((ch: EpgChannel) => ch.rowName === this.activeRowName)
        }
        return this._epg.EPGChannelList
    }

    _loadImg(url: string) {
        if (!url || this._currentImgUrl === url) return
        const imgNode = (this as any).tag('Image.Img')
        if (!imgNode) return
        const hadImage = !!this._currentImgUrl
        this._currentImgUrl = url
        if (hadImage) imgNode.blur = true
        clearTimeout(this.imageUpdateId)
        this.imageUpdateId = setTimeout(() => {
            imgNode.src = getImage(this._currentImgUrl)
            imgNode.signals = {
                'imageLoaded': () => {
                    imgNode.blur = false
                    imgNode.signals = {}
                }
            }
        }, 500)
    }

    /** Fired by EPGRowUI via fireAncestors on every Left/Right program navigation */
    $epgProgramSelected(tile: any) {
        if (this._getState() !== 'GridState' || this._channelIconFocused) return
        this.startAutoHideTimeout()
        if (tile) this.updateInfo(tile)
    }

    /** Called from EPGRowUI when Left is pressed from the first program */
    $focusChannelIcon() {
        this.startAutoHideTimeout()
        this._setState('ChannelIconState')
    }

    /** Transition to ThumbnailState while keeping the EPG expanded, storing where to return */
    _enterThumbnailKeepExpanded(returnState: string) {
        this._keepEpgExpanded = true
        this._epgReturnState = returnState
        this._setState('ThumbnailState')
    }

    /** Called when the focused row in ChannelFocusManager changes */
    _onChannelRowChanged(row: EPGRowUI) {
        if (this._getState() !== 'GridState') return;
        this.startAutoHideTimeout();

        // Sync channel icon column — plinko is disabled so we do this manually.
        const rowIndex = this._ChannelFocusManager.selectedIndex;
        this._TimelineChannelHeading.selectedIndex = rowIndex;

        // Non-focused rows have their selectedIndex kept in sync with focusTime by
        // _updateChannel(), which fires on every updateTransition call. By the time the
        // user presses Down/Up, the target row already points to the correct tile.
        // Do NOT set row.selectedIndex here — it triggers LUI Row's internal scroll
        // engine which overrides the externally-managed _Items.x (set by updateTransition),
        // causing the viewport to jump sideways.

        const selectedTile = row?.selected as any;
        const rowChannel = (row as any)._channel as EpgChannel;
        if (this._epg) {
            const currIndex = (rowChannel as any)?.index ?? rowIndex;
            const oldIndex = this.prevIndex;
            this._epg.currIndex = currIndex;
            this.prevIndex = currIndex;
            if (selectedTile && !this._channelIconFocused) {
                this.updateInfo(selectedTile);
            }
            this._epg.getNextPrefetchingIndex(currIndex, oldIndex);
        }

        // Fallback only for rows whose tiles haven't loaded yet. Once _doCreateTiles
        // runs it calls _getProgramUIIndex() which lands on the correct tile. This
        // setTimeout only fires when focus arrives before data does.
        if (!row?.isLoaded) {
            setTimeout(() => {
                if (!row?.items?.length || !this.focusTime) return;
                const selected = row.selected as any;
                if (selected && EPGUtility.isPlaying(selected.st, selected.et, this.focusTime)) return;
                const focusIndex = row.items.findIndex((p: any) =>
                    EPGUtility.isPlaying(p.st, p.et, this.focusTime)
                );
                if (focusIndex >= 0) {
                    row.selectedIndex = focusIndex;
                    const tile = row.selected as any;
                    if (tile && !this._channelIconFocused) this.updateInfo(tile);
                }
            }, 0);
        }
    }

    _update() {
        this._updateChannel()
        if (this.epgChannelCreated) {
            this._updateRowLabelHighlight(this.activeRowName)
        }
    }

    _updateChannel() {
        if (!this._epg) {
            return
        }

        if (this._epg && this.epgChannelCreated) {
            return
        }

        // With a row label the program area is 1421px → 470px/30min gives 3 half-hour
        // markers. Without a row label the area widens to 1766px; use 400px/30min
        // (800px/hour) and show only hourly markers so 3 labels land at 0, 800, 1600
        // with no partial 4th peeking in.
        const showRowLabel = this._showRowLabel;
        const s = this as any;
        if (showRowLabel) {
            EPGConfig.pixelPer30m = 470;
            EPGConfig.pixelPerHour = 940;
        } else {
            EPGConfig.pixelPer30m = 700;
            EPGConfig.pixelPerHour = 1400;
        }

        this._updateTimeline(this._epg.timerange)
        const channelHeadderList = []
        const rowLabelList = []

        if (this.allRowNames && this.allRowNames.length > 0) {
            this.allRowNames.forEach((rowName: string) => {
                rowLabelList.push({
                    type: EPGRowLabelUI,
                    rowName: rowName,
                    isActiveRow: (rowName === this.activeRowName)
                });
            });
        } else {
            this._epg.EPGChannelList.forEach((channel: EpgChannel) => {
                rowLabelList.push({
                    type: EPGRowLabelUI,
                    rowName: channel.rowName || '',
                    isActiveRow: (channel.rowName === this.activeRowName)
                });
            });
        }

        const filteredChannels = this._getFilteredChannels();

        const localSelectedIndex = this._getFilteredIndex(this._epg.currIndex, filteredChannels);
        this._playingLocalIndex = localSelectedIndex >= 0 ? localSelectedIndex : 0;

        const channelListUi = filteredChannels.map((channel: EpgChannel, index: number) => {
            channelHeadderList.push({
                type: EPGChannelIconUI,
                channelIdx: index,
                imgSrc: channel.squareIcon || channel.image,
                zIndex: 10,
            })
            return {
                y: index * EPGConfig.ChannelRowHeight,
                w: 300,
                h: EPGConfig.ChannelRowHeight,
                type: EPGRowUI,
                collision: 2,
                title: channel.name,
                direction: 'row',
                channelIndex: index,
                channel: channel,
                timerange: this._epg.timerange,
                focusTime: this.focusTime,
                signals: {
                    'selectedChange': 'selectedChange'
                },
                style: {
                    itemSpacing: 0
                }
            }
        })

        let activeRowIndex = 0;
        if (this.allRowNames && this.allRowNames.length > 0) {
            activeRowIndex = this.allRowNames.indexOf(this.activeRowName);
            if (activeRowIndex < 0) activeRowIndex = 0;
        }

        this._TimelineChannelHeading.items = channelHeadderList
        this._highlightedIconIdx = -1
        this._TimelineChannelHeading.selectedIndex = localSelectedIndex >= 0 ? localSelectedIndex : 0
        this._RowLabelColumn.items = rowLabelList
        this._RowLabelColumn.selectedIndex = activeRowIndex
        this._ChannelFocusManager.items = channelListUi
        this._ChannelFocusManager.items.forEach((row: EPGRowUI) => {
            if ((row as any).style) (row as any).style.itemTransition = { duration: 0 }
        })
        this._ChannelFocusManager.selectedIndex = localSelectedIndex >= 0 ? localSelectedIndex : 0
        this.epgChannelCreated = true;

        s.tag('EpgWrapper.RowLabelWrapper').visible = showRowLabel;
        s.tag('EpgWrapper.ChannleNameWrapper').patch({ x: showRowLabel ? 345 : 0 });
        const programWrapperW = showRowLabel ? PROGRAM_WRAPPER_W_WITH_ROW_LABEL : 1876 - EPGConfig.ChannelWidth;
        s.tag('EpgWrapper.ChannleProgramWrapper').patch({
            x: showRowLabel ? 455 : EPGConfig.ChannelWidth,
            w: programWrapperW
        });
        s.tag('EpgWrapper.ChannleProgramWrapper.TimelineArrowRight').patch({
            x: programWrapperW - TIMELINE_ARROW_RIGHT_INSET
        });
        const timelineW = showRowLabel ? 3 * EPGConfig.pixelPer30m : 1876 - EPGConfig.ChannelWidth;
        s.tag('EpgWrapper.ChannleProgramWrapper.Timeline').patch({ w: timelineW });
    }

    _updateRowLabelHighlight(activeRowName: string) {
        if (!activeRowName) return;
        const labelItems = this._RowLabelColumn?.items;
        if (labelItems) {
            labelItems.forEach((item: any) => {
                if (item && typeof item.patch === 'function') {
                    const shouldBeActive = item.rowName === activeRowName;
                    if (item.isActiveRow !== shouldBeActive) {
                        item.patch({ isActiveRow: shouldBeActive });
                    }
                }
            });
        }
    }

    _updateTimeline(timerange) {
        const key = timerange.startDate.getTime() * 1000 + EPGConfig.pixelPerHour
        if (!this._timelineCache || this._timelineCache.key !== key) {
            const channel = new EpgChannel()
            this._timelineCache = { key, items: channel.createTimeline(timerange) }
        }
        this._Timeline.items = this._timelineCache.items
    }

    _updateCurrentTime(currDate) {
        if (!this._epg) {
            return
        }
        let pos = EPGUtility.getCurrentTimelinePosition(this._epg.timerange, EPGConfig.pixelPerHour, currDate)
        this.tag('CurrTimeLine').x = pos

    }

    selectedChangeTimeline(selected) {
        const transitionX = selected.x
        this.updateTransition(transitionX, selected)
    }

    $requestRowSync(row: EPGRowUI) {
        if (this._Timeline && this._Timeline._Items && row._Items) {
            // Restore the row's X position to match the timeline, negating LUI's automatic scroll
            row._Items.x = this._Timeline._Items.x;
        }
    }

    $syncTimelineToProgram(st: Date) {
        const timeline = this.tag('Timeline');
        if (!timeline || !timeline.items || timeline.items.length === 0) return;

        let index = timeline.items.findIndex((block: any) => EPGUtility.isPlaying(block.st, block.et, st));
        if (index < 0) {
            if (st < timeline.items[0].st) {
                index = 0;
            }
        }

        if (index >= 0) {
            if (timeline.selectedIndex === index) {
                // Force transition update if the selection index didn't change
                const selectedBlock = timeline.items[index];
                this.selectedChangeTimeline(selectedBlock);
            } else {
                timeline.selectedIndex = index;
            }
        }
    }

    selectedChange(selected) {
        if (this._ChannelFocusManager && selected.channelIndex == this._ChannelFocusManager.selectedIndex) {
            const row = this._ChannelFocusManager.items[selected.channelIndex];
            const currIndex = row && row.channel ? row.channel.index : selected.channelIndex;
            const oldIndex = this.prevIndex
            // Never update the thumbnail from grid-navigation events while the thumbnail
            // is focused — ThumbnailState manages its own display via _updateNowPlayingThumbnail().
            if (!this._channelIconFocused && this._getState() !== 'ThumbnailState') {
                this.updateInfo(selected)
            } else if (this._getState() === 'ThumbnailState') {
                // EPG data just loaded for the now-playing row — re-run so category/rating
                // from the freshly parsed programs are reflected in the info panel.
                this._updateNowPlayingThumbnail()
            }
            this._TimelineChannelHeading.selectedIndex = selected.channelIndex
            this._epg.currIndex = currIndex
            this.prevIndex = currIndex
            this.startAutoHideTimeout()
            this._epg.getNextPrefetchingIndex(currIndex, oldIndex)
        }
    }

    _getFilteredIndex(globalIndex: number, filteredChannels: EpgChannel[]): number {
        return filteredChannels.findIndex((ch: EpgChannel) => ch.index === globalIndex);
    }

    selectedChangeRowLabel(selectedItem) {
        if (!selectedItem) return;
        const index = this._RowLabelColumn.selectedIndex;
        if (this._getState() === 'RowLabelState') {
            this.startAutoHideTimeout();
        }
        ;(this as any).fireAncestors('$epgRowLabelFocused', index);
    }

    updateEpgChannel(selected) {
        if (this._epg && this._epg.currIndex !== undefined) {
            const filteredChannels = this._getFilteredChannels();
            const localSelectedIndex = this._getFilteredIndex(this._epg.currIndex, filteredChannels);
            if (localSelectedIndex >= 0) {
                this._ChannelFocusManager.selectedIndex = localSelectedIndex;
                this._TimelineChannelHeading.selectedIndex = localSelectedIndex;
            }
        }
        if (this._getState() === 'ThumbnailState') {
            this._updateNowPlayingThumbnail();
        }
    }

    /** Keeps _playingLocalIndex in sync with the active channel independently of EPG grid browsing. */
    setNowPlayingIndex(localIndex: number) {
        this._playingLocalIndex = localIndex;
    }

    _updateNowPlayingThumbnail() {
        if (!this._epg) return;
        const filteredChannels = this._epg.EPGChannelList.filter(
            (ch: EpgChannel) => ch.rowName === this.activeRowName
        );
        const channel = filteredChannels[this._playingLocalIndex];
        if (!channel) return;

        const program = channel.getNowPlayingProgram(this._epg.currDate);
        const channelImage = channel.main_picture || channel.squareIcon || channel.image || '';
        if (program && program.title && program.title !== 'No Data') {
            this.updateInfo({
                title: program.title,
                // Fall back to channel artwork when program has no image so
                // updateImage never skips due to an empty URL.
                image: program.image || channelImage,
                category: program.category,
                rating: program.rating,
                st: program.start_timeObj,
                et: program.end_timeObj
            });
        } else {
            // Program not fetched yet — fall back to channel artwork
            this._updateChannelThumbnail(this._playingLocalIndex);
        }
    }

    _updateChannelThumbnail(idx: number) {
        const row = this._ChannelFocusManager?.items[idx]
        const channel: EpgChannel = row?.channel
        if (!channel) return
        const s = this as any
        s.tag('Title').text.text = channel.name
        s.tag('RatingBadge').visible = false
        s.tag('GenreText').text.text = ''
        s.tag('ProgressWrapper').visible = false

        this._loadImg(channel.image)
    }

    _restoreProgramThumbnail() {
        const selectedRow: EPGRowUI = this._ChannelFocusManager?.selected
        if (!selectedRow) return
        const selectedProgram = selectedRow.selected
        if (selectedProgram) {
            this.updateInfo(selectedProgram)
        }
    }

    updateInfo(selected) {
        this.tag('Title').text.text = selected.title

        const genre = selected.category || '';
        const rating = selected.rating || '';

        this.tag('GenreText').text.text = genre;
        if (rating) {
            this.tag('RatingBadge').patch({
                title: rating,
                visible: true
            });
            this.tag('GenreText').patch({ flexItem: { marginLeft: 35 } });
        } else {
            this.tag('RatingBadge').visible = false;
            this.tag('GenreText').patch({ flexItem: { marginLeft: 0 } });
        }

        const hasValidTime = selected.st && selected.et && selected.title !== 'No Data'

        const s = this as any
        if (hasValidTime) {
            s.tag('ProgressWrapper').visible = true
            s.tag('ProgStartTime').text.text = EPGUtility.formatAMPM(selected.st)
            s.tag('ProgEndTime').text.text = EPGUtility.formatAMPM(selected.et)
            const dur = selected.et - selected.st
            const currDate = this._epg?.currDate || selected.st
            const cur = currDate - selected.st
            let perc = Math.max(cur / dur, 0)
            perc = Math.min(perc, 1)
            s.tag('Progressbar').style = { height: 8, barColor: 0x1EFFFFFF, progressColor: this._getFocusColor(), radius: 0 }
            s.tag('Progressbar').progress = perc
        } else {
            s.tag('ProgressWrapper').visible = false
        }

        if (hasValidTime) {
            this._updateDateText(selected.st)
        }

        this.updateImage(selected.image)
    }

    /* Fix for XFIN-EPG-DATE: RowLabelHeading.DateText is built once in _template() from
       displayToday(), so it kept showing the launch day while the user scrolled right into
       tomorrow's programs. Re-derive it from whatever time slot is in view. */
    _updateDateText(date: Date) {
        if (!(date instanceof Date) || isNaN(date.getTime())) return
        const dateText = this.tag('DateText')
        if (!dateText) return
        const content = EPGUtility.displayDate(date)
        if (dateText.content !== content) {
            dateText.content = content
        }
    }

    updateImage(url) {
        this._loadImg(url)
    }

    updateTransition(transitionX: number, selected: any) {
        const pos = (transitionX * -1)

        if (pos > 0) return
        this.focusTime = EPGUtility.getFocusTime(selected.st)
        this.focusTime.setMinutes(this.focusTime.getMinutes()+15)
        this._updateDateText(selected.st)
        this.tag('CurrentTimeLineWrapper').x = pos;
        const viewportLeftEdge = -pos
        this._ChannelFocusManager.items.forEach((channel: EPGRowUI) => {
            channel.focusTime = this.focusTime
            channel.extendTilesIfNeeded()
            if (channel !== this._ChannelFocusManager.selected) {
                channel.trimOffscreenLeft(viewportLeftEdge)
                channel.loadMoreProgramsLeft(viewportLeftEdge)
            }
            channel._Items.x = pos;
        })
        this._Timeline._Items.x = pos;
    }

    override _handleEnter() {
        const selected: EPGRowUI = this._ChannelFocusManager.selected
        const program: any = selected.selected
        const isPlaying = EPGUtility.isPlaying(program.st, program.et, this._epg.currDate)
        this.fireAncestors('$changeChannel', selected._channelIndex)
    }

    _withoutScroll(fn: () => void) {
        const navs = [this._ChannelFocusManager, this._TimelineChannelHeading, this._RowLabelColumn].filter(Boolean)
        const prev = navs.map((nav: any) => nav._neverScroll)
        navs.forEach((nav: any) => { nav._neverScroll = true })
        try {
            fn()
        } finally {
            navs.forEach((nav: any, i: number) => { nav._neverScroll = prev[i] })
        }
    }

    $programTileClicked(channelIndex: number, program: any) {
        const row: EPGRowUI = this._ChannelFocusManager?.items[channelIndex]
        if (!row?.channel) return
        const tileIndex = row.items?.indexOf(program) ?? -1

        this._withoutScroll(() => {
            if (this._getState() !== 'GridState') this._setState('GridState')
            this._ChannelFocusManager.selectedIndex = channelIndex
            this._TimelineChannelHeading.selectedIndex = channelIndex
            if (tileIndex >= 0 && row.selectedIndex !== tileIndex) row.selectedIndex = tileIndex
        })

        if (program) {
            this.updateInfo(program)
        }
        ;(this as any).fireAncestors('$changeChannel', channelIndex)
        this.startAutoHideTimeout()
    }

    _handleClick(target: any) {
        const ref = target?.ref

        if (ref === 'TimelineArrowLeft' || ref === 'TimelineArrowRight') {
            const selectedRow: EPGRowUI = this._ChannelFocusManager?.selected
            if (!selectedRow || this._isLoading) return true
            if (this._getState() !== 'GridState') this._setState('GridState')
            this.startAutoHideTimeout()
            if (ref === 'TimelineArrowRight') {
                selectedRow.onRight()
            } else if (selectedRow.selectedIndex > 0 || selectedRow._loadTrimmedProgramsLeft()) {
                selectedRow.onLeft()
            }
            return true
        }

        if (ref !== 'FocusRing' && ref !== 'InfoIcon') return false
        if (this._isLoading) return true

        this._setState(ref === 'FocusRing' ? 'ThumbnailState' : 'InfoButtonState')
        this.stopAutoHideTimeout()
        ;(this as any).fireAncestors('$openChannelList')
        return true
    }

    _handleHover() {
        ;(this as any).fireAncestors('$expandEpg', true)
        this.startAutoHideTimeout()
        return false
    }

    $rowLabelClicked(rowName: string) {
        if (this._isLoading) return
        const idx = this.allRowNames?.indexOf(rowName) ?? -1
        if (idx < 0) return

        this._withoutScroll(() => {
            if (this._getState() !== 'RowLabelState') this._setState('RowLabelState')
            this._RowLabelColumn.selectedIndex = idx
        })
        this.startAutoHideTimeout()
        if (rowName !== this.activeRowName) {
            this._activeRowName = rowName
            this._updateRowLabelHighlight(rowName)
            this.epgChannelCreated = false
            ;(this as any).fireAncestors('$epgRowChanged', rowName)
        }
    }

    $rowLabelScrolled(direction: string) {
        if (this._isLoading || !this.allRowNames?.length) return
        if (!this._canScroll()) return
        const column = this._RowLabelColumn
        if (!column) return
        const cur = column.selectedIndex
        const max = this.allRowNames.length - 1
        const next = direction === 'up' ? Math.max(0, cur - 1) : Math.min(max, cur + 1)
        if (next !== cur) column.selectedIndex = next
        this.startAutoHideTimeout()
    }

    $channelIconClicked(idx: number) {
        const row = this._ChannelFocusManager?.items[idx]
        if (!row?.channel) return

        this._withoutScroll(() => {
            const prevIdx = this._ChannelFocusManager.selectedIndex
            this._ChannelFocusManager.selectedIndex = idx
            if (this._getState() !== 'ChannelIconState') {
                this._setState('ChannelIconState')
            } else {
                this._switchChannelIconFocus(prevIdx, idx)
            }
        })
        ;(this as any).fireAncestors('$changeChannel', idx)
        this.startAutoHideTimeout()
    }

    $channelListScrolled(direction: string) {
        if (this._isLoading || !this._canScroll()) return
        const oldIdx = this._ChannelFocusManager.selectedIndex
        const max = (this._ChannelFocusManager.items?.length ?? 1) - 1
        const newIdx = direction === 'up' ? Math.max(0, oldIdx - 1) : Math.min(max, oldIdx + 1)
        if (newIdx !== oldIdx) {
            this._ChannelFocusManager.selectedIndex = newIdx
            if (this._getState() === 'ChannelIconState') {
                this._switchChannelIconFocus(oldIdx, newIdx)
            } else if (this._TimelineChannelHeading) {
                this._TimelineChannelHeading.selectedIndex = newIdx
            }
        }
        this.startAutoHideTimeout()
    }

}
